import { Button } from './button';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioItem,
  DropdownMenuSubContent,
} from './dropdown-menu';
import { cn } from '../../lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { DESKTOP_MENU_SURFACE } from '../../lib/desktop-styles';

export { DESKTOP_MENU_SURFACE };

export function DesktopDropdownContent({
  className,
  sideOffset = 8,
  ...props
}: ComponentProps<typeof DropdownMenuContent>) {
  return (
    <DropdownMenuContent
      className={cn(DESKTOP_MENU_SURFACE, className)}
      sideOffset={sideOffset}
      {...props}
    />
  );
}

export function DesktopDropdownSubContent({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof DropdownMenuSubContent>) {
  return (
    <DropdownMenuSubContent
      className={cn(DESKTOP_MENU_SURFACE, className)}
      sideOffset={sideOffset}
      {...props}
    />
  );
}

export function DesktopDropdownItem({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuItem>) {
  return (
    <DropdownMenuItem
      className={cn('type-ui', className)}
      {...props}
    />
  );
}

export function DesktopDropdownRadioItem({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuRadioItem>) {
  return (
    <DropdownMenuRadioItem
      className={cn(
        'type-ui [&>span:first-child]:left-2.5',
        className,
      )}
      {...props}
    />
  );
}

export function DesktopDropdownLabel({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuLabel>) {
  return (
    <DropdownMenuLabel
      className={cn('type-caption', className)}
      {...props}
    />
  );
}

const navButtonVariants = cva(
  'type-ui flex min-w-0 w-full items-center gap-2 rounded-lg text-left leading-5 transition-colors',
  {
    variants: {
      active: {
        true: 'bg-accent/80 text-foreground',
        false: 'text-foreground/78 hover:bg-accent/55 hover:text-foreground',
      },
      density: {
        sidebar: 'h-[1.875rem] px-3.5 py-0',
        compact: 'px-2.5 py-1.5',
        default: 'px-2.5 py-2',
        comfortable: 'px-3 py-2.5',
      },
    },
    defaultVariants: { active: false, density: 'default' },
  },
);

export function DesktopIconButton({ className, ...props }: ComponentProps<typeof Button>) {
  return (
    <Button
      className={cn('text-muted-foreground hover:text-foreground', className)}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    />
  );
}

export function DesktopSectionLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'type-caption font-medium text-muted-foreground/70 uppercase tracking-wider',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function DesktopNavButton({
  active = false,
  className,
  density,
  ...props
}: ComponentProps<'button'> & VariantProps<typeof navButtonVariants>) {
  return (
    <Button
      variant="ghost"
      aria-current={active ? 'page' : undefined}
      className={cn(
        'h-auto justify-start whitespace-normal font-normal',
        navButtonVariants({ active, density }),
        className,
      )}
      type="button"
      {...props}
    />
  );
}

export function DesktopStatusPill({ children }: { children: ReactNode }) {
  return (
    <div className="type-caption flex items-center gap-1.5 rounded-sm border border-hairline bg-card px-2.5 py-1 text-muted-foreground">
      {children}
    </div>
  );
}
